// Configure ("Flight Plan") — ported pixel-true from prototype
// configure-merged.jsx Configure_Merged. Dark mission-control hero with
// embedded launch button + telemetry, mode-aware stage shape, and four
// stage panels (Profile · Scope · Stories · Pipeline). Stage 4 is the
// grouped 3-column pipeline picker with the AI custom prompt and Boost
// Data toggle.

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { MergeHint } from "@/components/configure/MergeHint";
import { TableauOutputsPanel } from "@/components/configure/TableauOutputsPanel";
import { MODES, PIPELINE_STEPS, VOLUMES } from "@/lib/data";
import { useAuth } from "@/hooks/useAuth";
import { useRuntime } from "@/hooks/useRuntime";
import { useConfigStore } from "@/lib/store/configStore";
import { useUiStore } from "@/lib/store/uiStore";
import type {
  Layer,
  ModeOption,
  Profile,
  RunConfig,
  Story,
  TableauOutputsConfig,
} from "@/lib/types";

const DEFAULT_TABLEAU_OUTPUTS: TableauOutputsConfig = {
  tdsx: true,
  twbx: false,
  publish: false,
  pulse: false,
  patProfileId: null,
  patOneOff: null,
};

interface StageDef {
  id: "pack" | "prereq" | "scope" | "stories" | "pipeline";
  label: string;
}

const LAYER_META: Record<
  "data" | "insight" | "activation",
  { label: string; blurb: string; fg: string; bg: string; border: string }
> = {
  data: {
    label: "Data layer",
    blurb: "Load or export raw records.",
    fg: "var(--eb-50)",
    bg: "#e7f0fa",
    border: "#bbd4ec",
  },
  insight: {
    label: "Insight layer",
    blurb: "Semantic model for metrics + AI.",
    fg: "var(--teal)",
    bg: "#dcefe2",
    border: "#a8d4b9",
  },
  activation: {
    label: "Activation",
    blurb: "Apps, flows, dashboards.",
    fg: "var(--violet)",
    bg: "#ede0fb",
    border: "#d2bcf3",
  },
};

const PIPELINE_DEPS: Partial<Record<Layer, Layer[]>> = {
  sdm: ["dc"],
  apps: ["dc"],
  tabnext: ["dc"],
};

export function Configure(): JSX.Element {
  const navigate = useNavigate();
  const { config, setConfig } = useConfigStore();
  const isRefresh = config.mode === "refresh";
  const isContinue = config.mode === "continue";
  const isCustom = config.mode === "custom";
  const isTdsxOnly = config.mode === "tdsx_only";

  const stages: StageDef[] = useMemo(() => {
    if (isRefresh) return [{ id: "pipeline", label: "What to refresh" }];
    if (isContinue) {
      return [
        { id: "stories", label: "Stories to land" },
        { id: "pipeline", label: "What to add" },
      ];
    }
    // TDSX-only output produces a downloadable file; no Salesforce org
    // is connected to and no prereq check is meaningful. Hide the stage
    // entirely so the wizard doesn't dead-end on a stale 404 message.
    const baseStages: StageDef[] = [
      { id: "pack", label: isCustom ? "Custom profile" : "Company profile" },
    ];
    if (!isTdsxOnly) {
      baseStages.push({ id: "prereq", label: "Org pre-reqs" });
    }
    baseStages.push(
      { id: "scope", label: "Scope & volume" },
      { id: "stories", label: "Stories to land" },
      { id: "pipeline", label: "Pipeline targets" },
    );
    return baseStages;
  }, [isRefresh, isContinue, isCustom, isTdsxOnly]);

  const [step, setStep] = useState(0);
  const mode = MODES.find((m) => m.id === config.mode);

  const { data: packs = [] } = useQuery({
    queryKey: ["packs"],
    queryFn: api.listPacks,
  });
  const { data: storiesData = [] } = useQuery({
    queryKey: ["stories"],
    queryFn: api.listStories,
  });

  const pack = packs.find((p) => p.id === config.profileId);
  const vol = VOLUMES.find((v) => v.id === config.volume);

  const storiesAvailable = storiesData.filter(
    (s) =>
      (config.salesCloud && s.cloud === "sales") ||
      (config.serviceCloud && s.cloud === "service"),
  ).length;
  const pipelineCount = Object.values(config.pipeline).filter(Boolean).length;
  // PR 1 Week 3: when the TDSX layer is on, Publish/Pulse require
  // either a saved profile or a one-off PAT. Block Launch otherwise
  // — the backend would 401 anyway, but blocking up front gives the
  // SE a clearer error path (the panel itself shows a red banner).
  const tableauCredsOk = (() => {
    if (!config.pipeline.tdsx) return true;
    const t = config.tableauOutputs;
    if (!t) return true;
    if (!t.publish && !t.pulse) return true;
    return !!t.patProfileId || !!t.patOneOff;
  })();

  const stepsValid: Record<StageDef["id"], boolean> = {
    pack: !!config.profileId || isCustom,
    prereq: true, // informational; never blocks launch
    scope: !!config.volume && (config.salesCloud || config.serviceCloud),
    stories: config.stories.length > 0 || isRefresh || isTdsxOnly,
    pipeline: pipelineCount > 0 && tableauCredsOk,
  };
  const allValid = stages.every((s) => stepsValid[s.id] !== false);

  const auth = useAuth();
  const { circuitBreaker } = useRuntime();
  const showToast = useUiStore((s) => s.showToast);
  const setAiGenOpen = useUiStore((s) => s.setAiGenOpen);
  const launch = useMutation({
    mutationFn: (cfg: RunConfig) => api.createRun(cfg),
    onSuccess: (run) => navigate(`/monitor/${run.id}`),
    onError: (e) => showToast(`Launch failed: ${(e as Error).message}`),
  });
  const handleLaunch = (): void => {
    if (!allValid || launch.isPending) return;
    if (circuitBreaker.engaged) {
      showToast(
        circuitBreaker.reason
          ? `Training paused: ${circuitBreaker.reason}`
          : "Training is briefly paused — please wait for the trainer to resume.",
      );
      return;
    }
    if (!auth.orgConnected) {
      showToast("Connect a Salesforce org first.");
      auth.connect();
      return;
    }
    launch.mutate(config);
  };

  const goStep = (id: StageDef["id"]): void => {
    const idx = stages.findIndex((s) => s.id === id);
    if (idx >= 0) setStep(idx);
  };

  const currentStage = stages[step] ?? stages[0];

  return (
    <div
      style={{
        maxWidth: 1080,
        margin: "0 auto",
        padding: "28px 24px 120px",
      }}
    >
      <MissionControlHero
        mode={mode}
        pack={pack ?? null}
        vol={vol ?? null}
        config={config}
        storiesAvailable={storiesAvailable}
        pipelineCount={pipelineCount}
        allValid={allValid}
        launching={launch.isPending}
        circuitBreakerEngaged={circuitBreaker.engaged}
        onJumpTo={goStep}
        onLaunch={handleLaunch}
        onBack={() => navigate("/")}
      />

      {/* Stage strip — joined to body */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          margin: "20px 0 0",
          background: "white",
          border: "1px solid var(--bd)",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        {stages.map((s, i) => {
          const active = i === step;
          const valid = stepsValid[s.id];
          return (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              style={{
                flex: 1,
                padding: "14px 16px",
                border: 0,
                borderRight:
                  i < stages.length - 1
                    ? "1px solid var(--bd-2)"
                    : "none",
                background: active ? "var(--cb-98)" : "white",
                textAlign: "left",
                position: "relative",
                cursor: "pointer",
              }}
            >
              {active && (
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: "var(--eb-50)",
                  }}
                />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background: valid
                      ? active
                        ? "var(--eb-50)"
                        : "#dcefe2"
                      : "var(--cb-95)",
                    color: valid
                      ? active
                        ? "white"
                        : "var(--teal)"
                      : "var(--ink-3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {valid && !active ? "✓" : i + 1}
                </div>
                <div>
                  <div className="label">Stage {i + 1}</div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: active ? "var(--eb-15)" : "var(--ink)",
                      marginTop: 1,
                    }}
                  >
                    {s.label}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Stage body */}
      <div
        className="card fade-up"
        key={step}
        style={{
          marginTop: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTop: "none",
          padding: 24,
        }}
      >
        {currentStage.id === "pack" && (
          <StagePack
            packs={packs}
            selected={config.profileId}
            isCustom={isCustom}
            onPick={(id) => setConfig({ profileId: id })}
            onAiGenerate={() => setAiGenOpen(true)}
            onManageProfiles={() => navigate("/profiles")}
          />
        )}
        {currentStage.id === "prereq" && (
          <StagePrereq
            config={config}
            onUpdate={setConfig}
          />
        )}
        {currentStage.id === "scope" && (
          <StageScope
            config={config}
            isTdsxOnly={isTdsxOnly}
            onUpdate={setConfig}
          />
        )}
        {currentStage.id === "stories" && (
          <StageStories
            stories={storiesData}
            picked={config.stories}
            salesCloud={config.salesCloud}
            serviceCloud={config.serviceCloud}
            isContinue={isContinue}
            onSelectAll={(ids) => setConfig({ stories: ids })}
            onClear={() => setConfig({ stories: [] })}
            onToggle={(id) =>
              setConfig((c) => ({
                ...c,
                stories: c.stories.includes(id)
                  ? c.stories.filter((x) => x !== id)
                  : [...c.stories, id],
              }))
            }
          />
        )}
        {currentStage.id === "pipeline" && (
          <StagePipeline
            config={config}
            insightsApp={pack?.insightsApp ?? null}
            onToggle={(k) =>
              setConfig((c) => ({
                ...c,
                pipeline: { ...c.pipeline, [k]: !c.pipeline[k] },
              }))
            }
            onPreset={(preset) => setConfig({ pipeline: preset })}
            onPromptChange={(v) => setConfig({ aiCustomPrompt: v })}
            onTableauOutputsChange={(next) =>
              setConfig({ tableauOutputs: next })
            }
          />
        )}

        {/* Footer nav */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 24,
            paddingTop: 18,
            borderTop: "1px solid var(--bd-2)",
          }}
        >
          <button
            className="btn ghost"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            ← Previous
          </button>
          <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
            Stage {step + 1} of {stages.length}
            {!stepsValid[currentStage.id] && (
              <span style={{ color: "var(--yellow)", marginLeft: 8 }}>
                · needs attention
              </span>
            )}
          </div>
          {step < stages.length - 1 ? (
            <button
              className="btn primary"
              onClick={() =>
                setStep((s) => Math.min(stages.length - 1, s + 1))
              }
            >
              Continue →
            </button>
          ) : (
            <button
              className="btn primary lg"
              onClick={handleLaunch}
              disabled={!allValid || launch.isPending || circuitBreaker.engaged}
              style={{
                boxShadow: "0 4px 14px -4px rgba(1,118,211,.45)",
              }}
              title={
                circuitBreaker.engaged
                  ? `Training paused: ${circuitBreaker.reason ?? ""}`
                  : undefined
              }
            >
              {launch.isPending
                ? "Launching…"
                : circuitBreaker.engaged
                  ? "Paused by trainer"
                  : "Launch pipeline"}
              <span style={{ fontSize: 14 }}>→</span>
            </button>
          )}
        </div>
      </div>
      {circuitBreaker.engaged && (
        <div
          role="status"
          style={{
            marginTop: 14,
            padding: "12px 16px",
            background: "linear-gradient(180deg,#fffbeb 0%,#fef3c7 100%)",
            border: "1px solid #fcd34d",
            borderRadius: 12,
            color: "#78350f",
            fontSize: 13,
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div
            aria-hidden
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: "#fbbf24",
              color: "#78350f",
              display: "grid",
              placeItems: "center",
              fontWeight: 800,
            }}
          >
            ⏸
          </div>
          <div>
            <strong>Training is briefly paused.</strong>{" "}
            {circuitBreaker.reason
              ? circuitBreaker.reason
              : "The trainer will resume new submissions in a few minutes."}{" "}
            In-flight jobs continue. You can keep tweaking your config — the
            Launch button will re-enable automatically when the pause is lifted.
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────── Mission control hero ───────────────────

interface HeroProps {
  mode?: ModeOption;
  pack: Profile | null;
  vol: typeof VOLUMES[number] | null;
  config: RunConfig;
  storiesAvailable: number;
  pipelineCount: number;
  allValid: boolean;
  launching: boolean;
  circuitBreakerEngaged: boolean;
  onJumpTo: (id: StageDef["id"]) => void;
  onLaunch: () => void;
  onBack: () => void;
}

function MissionControlHero({
  mode,
  pack,
  vol,
  config,
  storiesAvailable,
  pipelineCount,
  allValid,
  launching,
  circuitBreakerEngaged,
  onJumpTo,
  onLaunch,
  onBack,
}: HeroProps): JSX.Element {
  const cells: { k: string; v: string; jump: StageDef["id"] }[] = [
    {
      k: "Accounts",
      v: vol ? vol.accounts.toLocaleString() : "—",
      jump: "scope",
    },
    {
      k: "Opportunities",
      v: vol ? vol.opps.toLocaleString() : "—",
      jump: "scope",
    },
    {
      k: "Cases",
      v: vol ? vol.cases.toLocaleString() : "—",
      jump: "scope",
    },
    {
      k: "Stories",
      v: `${config.stories.length}${storiesAvailable ? ` of ${storiesAvailable}` : ""}`,
      jump: "stories",
    },
    {
      k: "Pipeline",
      v: `${pipelineCount} step${pipelineCount !== 1 ? "s" : ""}`,
      jump: "pipeline",
    },
  ];

  const isContinue = config.mode === "continue";
  const isRefresh = config.mode === "refresh";

  return (
    <div
      style={{
        position: "relative",
        padding: 22,
        borderRadius: 16,
        background:
          "linear-gradient(135deg, var(--eb-15) 0%, var(--eb-30) 55%, #1558a0 100%)",
        color: "white",
        overflow: "hidden",
        boxShadow: "0 8px 32px -12px rgba(3,45,96,.35)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={onBack}
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                color: "#AACBE2",
                background: "transparent",
                border: 0,
                padding: 0,
                cursor: "pointer",
              }}
            >
              ← Flight plan
            </button>
            <span
              style={{
                fontSize: 10,
                padding: "2px 9px",
                borderRadius: 999,
                background: "rgba(255,255,255,.14)",
                color: "#C9E3F6",
                fontWeight: 500,
              }}
            >
              Mode · {mode?.label ?? "Custom"}
            </span>
            {(isContinue || isRefresh) && config.continueRunId && (
              <span
                style={{
                  fontSize: 10,
                  padding: "2px 9px",
                  borderRadius: 999,
                  background: "rgba(245,166,35,.2)",
                  color: "#f5c770",
                }}
              >
                Layering on existing run
              </span>
            )}
            {/*
              2026-05-03 (C-MEDIUM): the previous label hard-coded
              "Draft · autosaved 12s ago" which lied to the user —
              there is no time-based autosave, and "12s ago" never
              changed regardless of how long the page had been open.
              The store IS persisted to sessionStorage via Zustand's
              ``persist`` middleware, so we tell the user the truth:
              their selections survive a tab refresh in this browser.
            */}
            <span style={{ fontSize: 10, color: "#AACBE2" }}>
              Draft kept in this browser
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {pack ? (
              // Solid white pill so dark/transparent pack logos read on the
              // dark hero gradient. The previous translucent-white treatment
              // collapsed dark-ink marks (e.g. the official Cirrus logo) into
              // a barely-visible smudge. Mirrors Monitor.tsx — see
              // KNOWN_ISSUES #5.
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(255,255,255,.4)",
                  boxShadow: "0 2px 6px rgba(0,0,0,.12)",
                  flexShrink: 0,
                }}
              >
                <img
                  src={pack.logo}
                  alt=""
                  style={{ height: 30, width: 30, objectFit: "contain" }}
                  onError={(e) => {
                    // 2026-05-03: fall back to the bundled Tableau mark on
                    // a logo 404 / wrong-content-type. The Configure hero
                    // is the most-visible surface in the app and a single
                    // community-pack with a stale logo_url leaves a broken
                    // image glyph live during demos. Guard against an
                    // infinite loop if /assets/tableau-logo.png itself
                    // fails by stamping a flag on the node.
                    const img = e.currentTarget;
                    if (img.dataset.fallbackApplied) return;
                    img.dataset.fallbackApplied = "1";
                    img.src = "/assets/tableau-logo.png";
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: "rgba(255,255,255,.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px dashed rgba(255,255,255,.3)",
                  fontSize: 20,
                }}
              >
                +
              </div>
            )}
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                {pack ? pack.name : "New custom profile"}
              </div>
              <div style={{ fontSize: 12, color: "#AACBE2" }}>
                {pack ? pack.industry : "Unnamed"} · {vol?.label} volume
              </div>
            </div>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <button
            className="btn primary lg"
            onClick={onLaunch}
            disabled={!allValid || launching || circuitBreakerEngaged}
            style={{
              background: allValid && !circuitBreakerEngaged
                ? "#fff"
                : "rgba(255,255,255,.18)",
              color: allValid && !circuitBreakerEngaged
                ? "var(--eb-15)"
                : "rgba(255,255,255,.5)",
              boxShadow: allValid && !circuitBreakerEngaged
                ? "0 4px 20px -4px rgba(255,255,255,.3)"
                : "none",
              border: "none",
            }}
          >
            <span>
              {launching
                ? "Launching…"
                : circuitBreakerEngaged
                  ? "Paused by trainer"
                  : "Launch pipeline"}
            </span>
            <span style={{ fontSize: 16 }}>→</span>
          </button>
          <div
            style={{ fontSize: 11, color: "#AACBE2", marginTop: 8 }}
          >
            ⌘↵ · ~{vol?.duration ?? "—"} min build
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 1,
          marginTop: 18,
          background: "rgba(255,255,255,.12)",
          borderRadius: 10,
          padding: 1,
        }}
      >
        {cells.map((c, i) => (
          <button
            key={c.k}
            onClick={() => onJumpTo(c.jump)}
            style={{
              padding: "10px 14px",
              background:
                "linear-gradient(180deg, rgba(3,45,96,.55) 0%, rgba(3,45,96,.7) 100%)",
              border: 0,
              color: "white",
              textAlign: "left",
              borderRadius:
                i === 0
                  ? "9px 0 0 9px"
                  : i === cells.length - 1
                    ? "0 9px 9px 0"
                    : 0,
              cursor: "pointer",
            }}
          >
            <div className="label" style={{ color: "#8BA4BE" }}>
              {c.k}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
              {c.v}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────── Stage 1 · Pack ───────────────────

// Stage 1 picker is grouped into three drawers — Built-in,
// Community, and My Drafts — instead of a flat 3-column grid. The flat
// grid couldn't scale past 3 tiles, which silently hid every
// AI-generated and community-approved pack the SE actually wanted to
// use 90% of the time. Each drawer is a horizontal scroller so we can
// add tiles forever without the page growing taller.
type PackBucket = "builtin" | "community" | "mine";

const BUCKET_META: Record<
  PackBucket,
  { label: string; blurb: string }
> = {
  builtin: {
    label: "Built-in",
    blurb: "Generator-maintained, always available",
  },
  community: {
    label: "Community",
    blurb: "Approved by curators, shared across SEs",
  },
  mine: {
    label: "My Drafts",
    blurb: "Yours — pick one, edit, or submit",
  },
};

const BUCKET_ORDER: PackBucket[] = ["builtin", "community", "mine"];

// Lifecycle pill copy for the My Drafts drawer. We only badge the
// non-obvious states; a vanilla `draft` doesn't need a chip because
// the drawer header already says "My Drafts".
const STATUS_PILL: Record<string, { label: string; tone: string }> = {
  submitted: { label: "Submitted", tone: "var(--eb-50)" },
  rejected: { label: "Needs revision", tone: "#b54708" },
  approved: { label: "Approved", tone: "var(--teal)" },
  featured: { label: "Featured", tone: "#8a5200" },
};

function bucketFor(p: Profile): PackBucket {
  // Built-in wins regardless of secondary flags. Use the same
  // detection rule as mapPackToProfile (status === "built_in" OR
  // type === "official") so we stay in lockstep with how the API
  // layer marks a pack as Generator-maintained.
  if (p.status === "built_in" || p.type === "official") return "builtin";
  if (p.status === "approved" || p.status === "featured") return "community";
  // Everything else (draft / submitted / rejected) is the SE's own
  // work — the backend already scopes those to the author.
  return "mine";
}

function StagePack({
  packs,
  selected,
  isCustom,
  onPick,
  onAiGenerate,
  onManageProfiles,
}: {
  packs: Profile[];
  selected: string;
  isCustom: boolean;
  onPick: (id: string) => void;
  onAiGenerate: () => void;
  onManageProfiles: () => void;
}): JSX.Element {
  // 2026-05-03 (C-MEDIUM): the previous "Build a custom company profile"
  // form had ``<input>``/``<textarea>`` controls with no ``value`` /
  // ``onChange`` props — typing into them did nothing, and the
  // "Save profile" button hard-coded ``onPick("cirrus_renewable_energy")``
  // which would silently swap the user's selection to a built-in
  // pack. ``mode === "custom"`` is only ever entered from the
  // Profiles / Templates / PackEditor flows, all of which set
  // ``config.profileId`` to the draft they're test-driving — so the
  // standalone "name a profile" form was both unreachable in practice
  // and wired to wrong behaviour. Replace it with a confirm-and-go
  // view that shows which draft the user is test-driving and lets
  // them swap to a different profile from the regular bucket grid.
  const customDraft = isCustom
    ? packs.find((p) => p.id === selected) ?? null
    : null;
  // Group once so we can render in BUCKET_ORDER and decide which
  // drawers to show / how to badge the My Drafts empty state.
  const grouped: Record<PackBucket, Profile[]> = {
    builtin: [],
    community: [],
    mine: [],
  };
  packs.forEach((p) => grouped[bucketFor(p)].push(p));

  return (
    <div>
      <StageHeader
        title={isCustom ? "Test-drive your custom profile" : "Pick a company profile"}
        blurb={
          isCustom
            ? "You're test-driving a custom profile. Confirm the right draft is highlighted below, or pick a different profile to switch."
            : "Profiles define the customer story — vocabulary, product catalog, industry flavor. Built-in packs are always available; the community library and your drafts live in the rows below."
        }
      />

      {customDraft && (
        <div
          style={{
            margin: "0 0 16px",
            padding: "10px 14px",
            border: "1px solid rgba(245,166,35,.5)",
            borderRadius: 8,
            background: "rgba(245,166,35,.08)",
            color: "var(--ink)",
            fontSize: 13,
          }}
        >
          <strong>Test-driving:</strong> {customDraft.name}
          {customDraft.industry ? ` · ${customDraft.industry}` : ""}{" "}
          <span style={{ color: "var(--ink-2)" }}>
            (draft profiles can run end-to-end; submit for review when you're happy with the result.)
          </span>
        </div>
      )}

      {/*
        gridTemplateColumns is `minmax(0, 1fr)` instead of the default
        implicit `auto` track. CSS grid items default to
        `min-width: auto` which means they EXPAND to fit their
        content's intrinsic width — so when PackDrawer's child is a
        flex row of 280px tiles, the grid item grows to ~292px × N
        wide and the scroller never gets a chance to clip. The
        Community row spilled past the card on 2026-04-23 because
        of this exact trap; `minmax(0, 1fr)` lets the track shrink
        below content width so `overflow-x: auto` on the inner
        scroller actually fires. See chat 2026-04-23 screenshot.
      */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 18 }}>
        {BUCKET_ORDER.map((bucket) => {
          const items = grouped[bucket];
          // Hide empty drawers — except My Drafts, which gets a slim
          // empty-state CTA so first-time SEs always see a path to
          // "make my own pack" without leaving the build flow.
          if (items.length === 0 && bucket !== "mine") return null;
          return (
            <PackDrawer
              key={bucket}
              bucket={bucket}
              items={items}
              selected={selected}
              onPick={onPick}
              onAiGenerate={onAiGenerate}
            />
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 14,
          marginTop: 18,
        }}
      >
        <button
          className="btn ghost sm"
          onClick={onManageProfiles}
          title="Open the Profiles library to fork, edit, submit, or rate packs."
        >
          Manage in Profiles →
        </button>
        <button
          className="btn primary sm"
          onClick={onAiGenerate}
          title="Open the AI Generator inline — describe a company and we'll draft a pack you can use right here without leaving Build."
        >
          + Generate with AI
        </button>
      </div>
    </div>
  );
}

function PackDrawer({
  bucket,
  items,
  selected,
  onPick,
  onAiGenerate,
}: {
  bucket: PackBucket;
  items: Profile[];
  selected: string;
  onPick: (id: string) => void;
  onAiGenerate: () => void;
}): JSX.Element {
  const meta = BUCKET_META[bucket];
  // Track whether the scroller is currently overflowing on either side
  // so the chevron buttons + edge fades only appear when there is
  // something to scroll to. macOS hides scrollbars by default, so
  // tile-cutoff alone wasn't reading as "more →" — SEs were missing
  // packs entirely. See screenshot in chat 2026-04-20.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<{
    left: boolean;
    right: boolean;
  }>({ left: false, right: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = (): void => {
      // Bail if the element hasn't laid out yet (clientWidth=0 on first
      // paint when a tab is rehydrating). The ResizeObserver below will
      // re-fire as soon as it does.
      if (el.clientWidth === 0) return;
      setScrollState({
        left: el.scrollLeft > 4,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    };
    // First measurement: defer to next frame so flexbox + image-decoded
    // tile widths are settled. Without this the initial pass occasionally
    // saw clientWidth === scrollWidth and hid the chevrons forever.
    const raf = window.requestAnimationFrame(update);
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // ResizeObserver picks up cases the resize event misses: parent
    // collapse/expand (drawers, accordions), font load shifting tile
    // intrinsic size, sidebar toggling, etc.
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      window.cancelAnimationFrame(raf);
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, [items.length]);

  const scrollByPage = (dir: 1 | -1): void => {
    const el = scrollerRef.current;
    if (!el) return;
    // Tile is 280px + 12px gap = 292px. Page by ~80% of the visible
    // width so the click reveals new tiles without losing context.
    const stride = Math.max(292, Math.floor(el.clientWidth * 0.8));
    el.scrollBy({ left: dir * stride, behavior: "smooth" });
  };

  return (
    // minWidth:0 is the defensive twin of the parent grid's
    // `minmax(0, 1fr)` track — together they guarantee the drawer
    // never expands past its allotted column even if a future
    // refactor wraps it in a different flex/grid parent that
    // forgets the explicit track. Without one of the two, the
    // 280px-flex tiles below escape the container and `overflow-x`
    // silently does nothing.
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <div
          className="label"
          style={{
            color: "var(--ink-2)",
            fontWeight: 700,
            letterSpacing: ".06em",
          }}
        >
          {meta.label}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
          · {meta.blurb}
        </div>
        {items.length > 0 ? (
          <div
            style={{
              fontSize: 10,
              color: "var(--ink-3)",
              marginLeft: "auto",
            }}
          >
            {items.length} {items.length === 1 ? "pack" : "packs"}
          </div>
        ) : null}
      </div>

      {items.length === 0 ? (
        <DrawerEmptyState bucket={bucket} onAiGenerate={onAiGenerate} />
      ) : (
        // minWidth:0 here matches the drawer root for the same
        // reason — guarantees the relative wrapper can't push past
        // its parent's allotted width, which is what was causing
        // chevrons + edge-fades to render off-screen on the right.
        <div style={{ position: "relative", minWidth: 0 }}>
          <div
            ref={scrollerRef}
            style={{
              // overflow-x: auto + a fixed tile width gives us infinite
              // horizontal scroll. scroll-snap keeps the tiles aligned
              // when the SE flicks with a trackpad.
              display: "flex",
              gap: 12,
              overflowX: "auto",
              overflowY: "hidden",
              scrollSnapType: "x mandatory",
              paddingBottom: 4,
              scrollbarWidth: "thin",
            }}
          >
            {items.map((p) => (
              <PackTile
                key={p.id}
                pack={p}
                selected={selected === p.id}
                onPick={onPick}
                showStatusPill={bucket === "mine"}
              />
            ))}
          </div>

          {/* Edge fades — purely decorative, hint that more is hidden
              behind the scroll edge. pointer-events:none so the tile
              under the fade stays clickable. */}
          {scrollState.left && <ScrollEdgeFade side="left" />}
          {scrollState.right && <ScrollEdgeFade side="right" />}

          {/* Click-targets that page the row 80% of viewport. Keyboard
              users can still tab into the row + arrow-key it; these
              are pointer affordances. */}
          {scrollState.left && (
            <ScrollChevron side="left" onClick={() => scrollByPage(-1)} />
          )}
          {scrollState.right && (
            <ScrollChevron side="right" onClick={() => scrollByPage(1)} />
          )}
        </div>
      )}
    </div>
  );
}

function ScrollEdgeFade({ side }: { side: "left" | "right" }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        bottom: 4,
        width: 36,
        ...(side === "left" ? { left: 0 } : { right: 0 }),
        background: `linear-gradient(to ${side === "left" ? "right" : "left"}, white 0%, rgba(255,255,255,0) 100%)`,
        pointerEvents: "none",
      }}
    />
  );
}

function ScrollChevron({
  side,
  onClick,
}: {
  side: "left" | "right";
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Scroll packs left" : "Scroll packs right"}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: 32,
        height: 32,
        borderRadius: 999,
        border: "1px solid var(--bd)",
        background: "white",
        boxShadow: "0 2px 10px rgba(3,45,96,.18)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        lineHeight: 1,
        color: "var(--ink-2)",
        zIndex: 2,
        ...(side === "left" ? { left: -10 } : { right: -10 }),
      }}
    >
      {side === "left" ? "‹" : "›"}
    </button>
  );
}

function DrawerEmptyState({
  bucket,
  onAiGenerate,
}: {
  bucket: PackBucket;
  onAiGenerate: () => void;
}): JSX.Element {
  const isMine = bucket === "mine";
  return (
    <button
      onClick={isMine ? onAiGenerate : undefined}
      disabled={!isMine}
      style={{
        width: "100%",
        padding: "14px 16px",
        borderRadius: 10,
        border: "1px dashed var(--bd)",
        background: "var(--bg)",
        color: "var(--ink-2)",
        fontSize: 12,
        textAlign: "left",
        cursor: isMine ? "pointer" : "default",
      }}
    >
      {isMine
        ? "+ No drafts yet — generate one with AI to start your library."
        : "Nothing here yet."}
    </button>
  );
}

function PackTile({
  pack: p,
  selected: sel,
  onPick,
  showStatusPill,
}: {
  pack: Profile;
  selected: boolean;
  onPick: (id: string) => void;
  showStatusPill: boolean;
}): JSX.Element {
  const statusPill =
    showStatusPill && p.status ? STATUS_PILL[p.status] : undefined;
  return (
    <button
      key={p.id}
      onClick={() => onPick(p.id)}
      style={{
        // Fixed width + flex-shrink:0 = stable tile sizing inside the
        // horizontal scroller. 280px matches the old 3-column grid
        // tile width on a typical desktop.
        flex: "0 0 280px",
        scrollSnapAlign: "start",
        padding: 16,
        borderRadius: 10,
        textAlign: "left",
        border: `1px solid ${sel ? "var(--eb-50)" : "var(--bd)"}`,
        background: sel ? "#eef6fc" : "white",
        boxShadow: sel
          ? "0 4px 16px -6px rgba(1,118,211,.35)"
          : "none",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          minHeight: 28,
        }}
      >
        <img
          src={p.logo}
          alt=""
          style={{ height: 28 }}
          onError={(e) => {
            // 2026-05-03: same Tableau-mark fallback as the hero in
            // MissionControlHero (~line 543). Stage 1's pack tile is
            // the second-most-visible logo surface; a 404 on a
            // community pack's logo_url should not render a broken
            // image to every trainee on the picker.
            const img = e.currentTarget;
            if (img.dataset.fallbackApplied) return;
            img.dataset.fallbackApplied = "1";
            img.src = "/assets/tableau-logo.png";
          }}
        />
        {sel ? (
          <span
            className="chip on"
            style={{ fontSize: 10, padding: "2px 8px" }}
          >
            ✓ Selected
          </span>
        ) : statusPill ? (
          <span
            style={{
              fontSize: 10,
              padding: "2px 8px",
              borderRadius: 999,
              background: "white",
              border: `1px solid ${statusPill.tone}`,
              color: statusPill.tone,
              fontWeight: 600,
            }}
          >
            {statusPill.label}
          </span>
        ) : p.featured ? (
          <span
            className="chip soft-yellow"
            style={{ fontSize: 10, padding: "2px 8px" }}
          >
            Featured
          </span>
        ) : null}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--ink)",
        }}
      >
        {p.name}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--ink-3)",
          margin: "3px 0 8px",
        }}
      >
        {p.industry} · {p.products ?? "—"} products · {p.stories} stories
      </div>
      <p
        style={{
          fontSize: 11,
          color: "var(--ink-2)",
          lineHeight: 1.5,
          margin: 0,
          // Clamp to 3 lines so tiles in a drawer stay roughly the
          // same height — keeps the horizontal row visually clean.
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {p.description}
      </p>
    </button>
  );
}

// ─────────────────── Stage 2 · Scope ───────────────────

function StageScope({
  config,
  isTdsxOnly,
  onUpdate,
}: {
  config: RunConfig;
  isTdsxOnly: boolean;
  onUpdate: (mut: Partial<RunConfig>) => void;
}): JSX.Element {
  const vol = VOLUMES.find((v) => v.id === config.volume);

  return (
    <div>
      <StageHeader
        title="Shape the dataset"
        blurb={
          isTdsxOnly
            ? "TDSX-only mode always includes both clouds — pick volume only."
            : "Volume sets record counts and build time. Cloud selection drives which stories are available."
        }
      />

      <div className="label" style={{ marginBottom: 10 }}>
        Volume
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${VOLUMES.length}, 1fr)`,
          gap: 6,
        }}
      >
        {VOLUMES.map((v) => {
          const sel = config.volume === v.id;
          return (
            <button
              key={v.id}
              onClick={() => onUpdate({ volume: v.id })}
              style={{
                padding: "12px 8px",
                borderRadius: 8,
                border: `1px solid ${sel ? "var(--eb-50)" : "var(--bd)"}`,
                background: sel ? "var(--eb-50)" : "white",
                color: sel ? "white" : "var(--ink-2)",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <div>{v.label}</div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 400,
                  opacity: 0.85,
                  marginTop: 3,
                }}
              >
                {v.duration} min
              </div>
            </button>
          );
        })}
      </div>
      {vol && (
        <div
          style={{
            fontSize: 11,
            color: "var(--ink-2)",
            marginTop: 8,
            padding: "8px 12px",
            background: "var(--cb-98)",
            borderRadius: 8,
          }}
        >
          <strong style={{ color: "var(--ink)" }}>{vol.label}</strong> —{" "}
          {vol.desc}. Build time ~{vol.duration} min.
        </div>
      )}

      {!isTdsxOnly && (
        <>
          <div className="label" style={{ margin: "22px 0 10px" }}>
            Clouds
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {(
              [
                {
                  k: "salesCloud" as const,
                  title: "Sales Cloud",
                  sub: "Opportunities, leads, pipeline, forecasts",
                  color: "var(--eb-50)",
                  bg: "#eef6fc",
                },
                {
                  k: "serviceCloud" as const,
                  title: "Service Cloud",
                  sub: "Cases, agent work, SLAs, knowledge",
                  color: "var(--violet)",
                  bg: "#f6eefd",
                },
              ]
            ).map((c) => (
              <button
                key={c.k}
                onClick={() => onUpdate({ [c.k]: !config[c.k] })}
                style={{
                  textAlign: "left",
                  padding: 14,
                  borderRadius: 10,
                  border: `1px solid ${config[c.k] ? c.color : "var(--bd)"}`,
                  background: config[c.k] ? c.bg : "white",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Checkbox on={config[c.k]} color={c.color} size={16} />
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--ink)",
                      }}
                    >
                      {c.title}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--ink-2)",
                        marginTop: 2,
                      }}
                    >
                      {c.sub}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          marginTop: 22,
          padding: "10px 14px",
          background: "var(--cb-98)",
          borderRadius: 8,
          fontSize: 11,
          color: "var(--ink-3)",
        }}
      >
        Tip: Smart merge mode and the AI data boost are configured on the
        previous step (Org pre-reqs).
      </div>
    </div>
  );
}

// ─────────────────── Stage 2 · Org pre-reqs ───────────────────

// Languages the prod app's data generators ship localized strings for.
// Keep in sync with app/services/org_prereq_checker.py supported list.
const SUPPORTED_LANGS = [
  "en_US", "fr", "de", "es", "it", "pt_BR", "nl_NL",
  "th", "vi", "ja", "ko",
];

const LANG_NAMES: Record<string, string> = {
  en_US: "English (US)",
  en_GB: "English (UK)",
  fr: "French",
  de: "German",
  es: "Spanish",
  it: "Italian",
  pt_BR: "Portuguese (BR)",
  nl_NL: "Dutch",
  th: "Thai",
  vi: "Vietnamese",
  ja: "Japanese",
  ko: "Korean",
  zh_CN: "Chinese (Simplified)",
  zh_TW: "Chinese (Traditional)",
};

function languageLabel(code?: string): string | null {
  if (!code) return null;
  return LANG_NAMES[code] ?? code;
}

function StagePrereq({
  config,
  onUpdate,
}: {
  config: RunConfig;
  onUpdate: (mut: Partial<RunConfig>) => void;
}): JSX.Element {
  const auth = useAuth();
  const orgConnected = auth.orgConnected;
  const vol = VOLUMES.find((v) => v.id === config.volume);
  // Cached org inventory drives the contextual merge-mode hints. The
  // backend cache is 60s; 30s staleTime here means the picker re-fetches
  // at most once per Configure visit. Disabled until an org is actually
  // connected — there's no inventory to fetch otherwise.
  const { data: inventory = null } = useQuery({
    queryKey: ["org-inventory-detail"],
    queryFn: () => api.getOrgInventory(),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: orgConnected,
  });
  // Fire prereq check lazily once an org is connected. The endpoint
  // does real SOQL counts, so we fetch on mount and re-check only when
  // the user clicks "Re-check".
  const [prereq, setPrereq] = useState<{
    can_proceed?: boolean;
    has_warnings?: boolean;
    has_existing_data?: boolean;
    existing_accounts?: number;
    existing_opportunities?: number;
    existing_cases?: number;
    org_name?: string;
    edition?: string;
    language?: string;
    summary?: { pass: number; fail: number; warn: number; skip: number };
    checks?: { id: string; label: string; status: string; message?: string }[];
  } | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async (): Promise<void> => {
    if (!orgConnected) return;
    setChecking(true);
    setError(null);
    try {
      const r = await fetch(
        `/api/org/prereq-check?include_sales=${config.salesCloud}&include_service=${config.serviceCloud}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`prereq check failed (${r.status})`);
      setPrereq(await r.json());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setChecking(false);
    }
  };

  // Run on mount + when cloud selection changes.
  useEffect(() => {
    void runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgConnected, config.salesCloud, config.serviceCloud]);

  return (
    <div>
      <StageHeader
        title="Org pre-reqs"
        blurb="Before we provision anything we check the connected org for licenses, permissions, and existing data. Pick how Generator should treat what's already there."
      />

      {!orgConnected && (
        <div
          className="card"
          style={{
            padding: 14,
            background: "var(--cb-98)",
            border: "1px dashed var(--bd)",
            color: "var(--ink-2)",
            fontSize: 12,
          }}
        >
          No org connected. Connect a Salesforce org to run pre-req checks.
          <button
            className="btn primary sm"
            style={{ marginLeft: 12 }}
            onClick={() => auth.connect()}
          >
            Connect org →
          </button>
        </div>
      )}

      {orgConnected && (
        <>
          {/* Org snapshot */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <PrereqCell
              k="Org"
              v={prereq?.org_name ?? "—"}
              tone="default"
            />
            <PrereqCell
              k="Edition"
              v={prereq?.edition ?? "—"}
              tone="default"
            />
            <PrereqCell
              k="Language"
              v={languageLabel(prereq?.language) ?? "—"}
              tone={prereq?.language && !SUPPORTED_LANGS.includes(prereq.language) ? "warn" : "default"}
            />
            <PrereqCell
              k="Accounts in org"
              v={prereq?.existing_accounts?.toLocaleString() ?? "—"}
              tone={(prereq?.existing_accounts ?? 0) > 0 ? "warn" : "default"}
            />
            <PrereqCell
              k="Opportunities in org"
              v={prereq?.existing_opportunities?.toLocaleString() ?? "—"}
              tone={(prereq?.existing_opportunities ?? 0) > 0 ? "warn" : "default"}
            />
          </div>
          {prereq?.language && (
            <div
              style={{
                fontSize: 11,
                color: "var(--ink-3)",
                marginBottom: 14,
                paddingLeft: 4,
              }}
            >
              Generator will load demo data using strings localized for{" "}
              <strong style={{ color: "var(--ink-2)" }}>
                {languageLabel(prereq.language)}
              </strong>
              {SUPPORTED_LANGS.includes(prereq.language)
                ? " (matches the org's default language)."
                : ` — this language isn't fully supported, so data will fall back to English.`}
            </div>
          )}

          {/* Checks summary */}
          <div
            className="card"
            style={{
              padding: 14,
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                {checking
                  ? "Running pre-req checks…"
                  : error
                    ? `Check failed: ${error}`
                    : prereq?.can_proceed
                      ? "All required checks passed"
                      : prereq
                        ? "One or more required checks failed"
                        : "Pre-req results will appear here"}
              </div>
              {prereq?.summary && (
                <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                  {prereq.summary.pass} pass · {prereq.summary.warn} warn ·{" "}
                  {prereq.summary.fail} fail · {prereq.summary.skip} skip
                </div>
              )}
            </div>
            <button
              className="btn ghost sm"
              onClick={runCheck}
              disabled={checking}
            >
              {checking ? "Checking…" : "Re-check"}
            </button>
          </div>

          {/* Per-check details (collapsible) */}
          {(prereq?.checks?.length ?? 0) > 0 && (
            <details
              style={{
                marginBottom: 18,
                border: "1px solid var(--bd-2)",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontSize: 12,
                  color: "var(--ink-2)",
                  fontWeight: 600,
                }}
              >
                Show {prereq?.checks?.length} pre-req checks
              </summary>
              <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
                {prereq?.checks?.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      fontSize: 11,
                      display: "flex",
                      gap: 8,
                      alignItems: "baseline",
                      padding: "4px 0",
                    }}
                  >
                    <span
                      style={{
                        color:
                          c.status === "pass"
                            ? "var(--teal)"
                            : c.status === "warn"
                              ? "var(--orange)"
                              : c.status === "fail"
                                ? "var(--red)"
                                : "var(--ink-3)",
                        fontWeight: 700,
                      }}
                    >
                      {c.status === "pass"
                        ? "✓"
                        : c.status === "fail"
                          ? "✗"
                          : c.status === "warn"
                            ? "!"
                            : "·"}
                    </span>
                    <span style={{ color: "var(--ink)" }}>{c.label}</span>
                    {c.message && (
                      <span style={{ color: "var(--ink-3)" }}>· {c.message}</span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {/* Smart Merge Mode v3 — always-on tone tint so the
          recommendation reads at a glance; selection uses a 2px border
          + bolder bg + check-mark so the tile can never visually
          collapse.  Tones are driven by `prereq.has_existing_data`:
            • Add alongside  → green when org is empty, yellow when data
              exists (mixing real + demo data is the risky combo).
            • Smart merge    → green (recommended either way).
            • Clean & reload → always red (destructive). */}
      <div className="label" style={{ margin: "8px 0 10px" }}>
        Smart Merge Mode
      </div>
      <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 10 }}>
        How should we treat data that's already in the org?
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {(() => {
          const hasData = !!prereq?.has_existing_data;
          const tones = {
            green: {
              border: "#7ec99b",
              borderOn: "var(--teal)",
              bgIdle: "#f1faf4",
              bgOn: "#daf0e2",
              fg: "var(--chip-green-fg)",
            },
            yellow: {
              border: "#e8c47a",
              borderOn: "var(--yellow)",
              bgIdle: "#fef8e6",
              bgOn: "#fdedc4",
              fg: "var(--chip-yellow-fg)",
            },
            red: {
              border: "#e8a3a9",
              borderOn: "var(--pink)",
              bgIdle: "#fdf0f1",
              bgOn: "#fbdde0",
              fg: "var(--chip-red-fg)",
            },
          } as const;
          const options = [
            {
              k: "additive" as const,
              l: "Add alongside",
              tag: hasData
                ? "Records added next to your existing org data — mixed result."
                : "Safe — records added, nothing touched.",
              tone: hasData ? tones.yellow : tones.green,
              hint: hasData ? "Caution" : "Safe",
            },
            {
              k: "smart" as const,
              l: "Smart merge",
              tag: "Records with matching External_ID are upserted.",
              tone: tones.green,
              hint: "Recommended",
            },
            {
              k: "clean" as const,
              l: "Clean & reload",
              tag: "Destructive — wipes existing demo data first, then loads fresh.",
              tone: tones.red,
              hint: "Destructive",
            },
          ];
          return options.map((m) => {
            const on = config.mergeMode === m.k;
            return (
              <button
                key={m.k}
                onClick={() => onUpdate({ mergeMode: m.k })}
                style={{
                  position: "relative",
                  padding: "12px 14px",
                  borderRadius: 10,
                  textAlign: "left",
                  cursor: "pointer",
                  border: `${on ? 2 : 1}px solid ${on ? m.tone.borderOn : m.tone.border}`,
                  background: on ? m.tone.bgOn : m.tone.bgIdle,
                  outline: "none",
                  boxShadow: on
                    ? `0 0 0 3px ${m.tone.bgIdle}, 0 1px 2px rgba(0,0,0,.04)`
                    : "0 1px 1px rgba(0,0,0,.02)",
                  transition: "background .15s ease, border-color .15s ease, box-shadow .15s ease",
                  minHeight: 84,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--ink)",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {on && (
                      <span
                        aria-hidden
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 14,
                          height: 14,
                          borderRadius: 999,
                          background: m.tone.borderOn,
                          color: "white",
                          fontSize: 9,
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        ✓
                      </span>
                    )}
                    {m.l}
                  </div>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: ".06em",
                      textTransform: "uppercase",
                      color: "white",
                      background: m.tone.borderOn,
                      padding: "2px 7px",
                      borderRadius: 999,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.hint}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ink-2)",
                    marginTop: 6,
                    lineHeight: 1.4,
                  }}
                >
                  {m.tag}
                </div>
                <MergeHint
                  mode={m.k}
                  inventory={inventory}
                  requestedPackId={config.profileId ?? null}
                  requestedVolumeAccountTarget={vol?.accounts ?? 0}
                  requestedVolumeName={vol?.label ?? ""}
                />
              </button>
            );
          });
        })()}
      </div>

      {/* Boost data */}
      <div
        style={{
          marginTop: 22,
          padding: 14,
          borderRadius: 10,
          border: "1px solid " + (config.boostData ? "var(--violet)" : "var(--bd)"),
          background: config.boostData
            ? "linear-gradient(135deg, #f4ecfd, #fef6e3)"
            : "white",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            flexShrink: 0,
            background: config.boostData ? "var(--violet)" : "#ede0fb",
            color: config.boostData ? "white" : "var(--violet)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            fontWeight: 800,
          }}
        >
          ✦
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
            Boost your Data
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-2)",
              marginTop: 2,
              lineHeight: 1.45,
            }}
          >
            Run AI contrast pass — sharpen account names, deepen product
            descriptions, humanize case summaries. +2–3 min build.
          </div>
        </div>
        <button
          onClick={() => onUpdate({ boostData: !config.boostData })}
          aria-pressed={config.boostData}
          style={{
            width: 44,
            height: 26,
            borderRadius: 999,
            border: 0,
            padding: 2,
            cursor: "pointer",
            background: config.boostData ? "var(--violet)" : "var(--cb-80)",
            flexShrink: 0,
            position: "relative",
            transition: "background .15s",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: config.boostData ? 21 : 3,
              width: 20,
              height: 20,
              borderRadius: 999,
              background: "white",
              transition: "left .15s",
              boxShadow: "0 1px 3px rgba(0,0,0,.2)",
            }}
          />
        </button>
      </div>
    </div>
  );
}

function PrereqCell({
  k,
  v,
  tone,
}: {
  k: string;
  v: string | number;
  tone: "default" | "warn";
}): JSX.Element {
  return (
    <div
      style={{
        padding: "10px 12px",
        background: tone === "warn" ? "rgba(243,154,55,.08)" : "var(--cb-98)",
        borderRadius: 8,
        border: `1px solid ${tone === "warn" ? "rgba(243,154,55,.3)" : "var(--bd-2)"}`,
      }}
    >
      <div className="label" style={{ color: "var(--ink-3)" }}>{k}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginTop: 2 }}>
        {v}
      </div>
    </div>
  );
}

// ─────────────────── Stage 3 · Stories ───────────────────

function StageStories({
  stories,
  picked,
  salesCloud,
  serviceCloud,
  isContinue,
  onSelectAll,
  onClear,
  onToggle,
}: {
  stories: Story[];
  picked: string[];
  salesCloud: boolean;
  serviceCloud: boolean;
  isContinue: boolean;
  onSelectAll: (ids: string[]) => void;
  onClear: () => void;
  onToggle: (id: string) => void;
}): JSX.Element {
  const cloudVisible = stories.filter(
    (s) =>
      (salesCloud && s.cloud === "sales") ||
      (serviceCloud && s.cloud === "service"),
  );

  // Tag filter — additive UX. Defaults to "All", so existing flows stay
  // identical. The prototype had this; v2 dropped it. Restoring it lets
  // power users narrow long story libraries (Forecasting, Service,
  // Agents, …) without changing the default behavior.
  const allTags = useMemo(() => {
    const set = new Set<string>();
    cloudVisible.forEach((s) => (s.tags ?? []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [cloudVisible]);
  const [tagFilter, setTagFilter] = useState<string>("all");
  // Reset the tag filter when the cloud selection changes — otherwise a
  // tag that no longer exists in the new cloud would silently hide
  // every story.
  useEffect(() => {
    if (tagFilter !== "all" && !allTags.includes(tagFilter)) {
      setTagFilter("all");
    }
  }, [allTags, tagFilter]);

  const visible =
    tagFilter === "all"
      ? cloudVisible
      : cloudVisible.filter((s) => (s.tags ?? []).includes(tagFilter));

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 14,
        }}
      >
        <StageHeader
          title="Choose stories to land"
          blurb={
            isContinue
              ? "Add new narratives to the existing org — dashboards will pick these up."
              : `Each story is a narrative the dashboards tell. Curated from your ${[
                  salesCloud && "Sales",
                  serviceCloud && "Service",
                ]
                  .filter(Boolean)
                  .join(" + ")} Cloud selection.`
          }
          inline
        />
        <div style={{ display: "flex", gap: 6 }}>
          <button
            className="btn ghost sm"
            onClick={() => onSelectAll(visible.map((s) => s.id))}
          >
            Select all
          </button>
          <button className="btn ghost sm" onClick={onClear}>
            Clear
          </button>
        </div>
      </div>
      {allTags.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 12,
          }}
        >
          <span
            className="label"
            style={{ marginRight: 4, fontSize: 9 }}
          >
            Filter by tag
          </span>
          <button
            type="button"
            onClick={() => setTagFilter("all")}
            className={tagFilter === "all" ? "chip on" : "chip"}
            style={{ cursor: "pointer", border: "none" }}
          >
            All ({cloudVisible.length})
          </button>
          {allTags.map((t) => {
            const count = cloudVisible.filter((s) =>
              (s.tags ?? []).includes(t),
            ).length;
            const on = tagFilter === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTagFilter(t)}
                className={on ? "chip on" : "chip"}
                style={{ cursor: "pointer", border: "none" }}
              >
                {t} ({count})
              </button>
            );
          })}
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 6,
        }}
      >
        {visible.map((s) => {
          const on = picked.includes(s.id);
          const tagText = s.tags?.length ? `Audience: ${s.tags.join(", ")}` : "";
          return (
            <div
              key={s.id}
              style={{
                position: "relative",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${on ? "var(--eb-50)" : "var(--bd)"}`,
                background: on ? "#eef6fc" : "white",
              }}
            >
              <button
                onClick={() => onToggle(s.id)}
                style={{
                  appearance: "none",
                  border: 0,
                  background: "transparent",
                  padding: 0,
                  textAlign: "left",
                  width: "100%",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 3,
                  }}
                >
                  <Checkbox on={on} color="var(--eb-50)" size={14} />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink)",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {s.title}
                  </span>
                  <span
                    className="chip"
                    style={{ fontSize: 9, padding: "1px 6px" }}
                  >
                    {s.cloud}
                  </span>
                  <span
                    title={`${s.title}\n\n${s.desc}\n\n${tagText}`}
                    aria-label="Story details"
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 999,
                      background: "var(--cb-95)",
                      color: "var(--ink-3)",
                      fontSize: 10,
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "help",
                    }}
                  >
                    i
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ink-2)",
                    lineHeight: 1.4,
                  }}
                >
                  {s.desc}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────── Stage 4 · Pipeline ───────────────────

const PIPELINE_PRESETS: Record<string, RunConfig["pipeline"]> = {
  crm: {
    crm: true,
    tdsx: false,
    dc: false,
    sdm: false,
    apps: false,
    tabnext: false,
    refresh: false,
  },
  tdsx: {
    crm: false,
    tdsx: true,
    dc: false,
    sdm: false,
    apps: false,
    tabnext: false,
    refresh: false,
  },
  // ``full`` enables every layer including C360 SDM and the Insights
  // Apps stack. Both ATF paths are GA per 2026-05-12; see
  // ``docs/GA_READINESS_HANDOFF.md``.
  full: {
    crm: true,
    tdsx: false,
    dc: true,
    sdm: true,
    apps: true,
    tabnext: true,
    refresh: false,
  },
};

function StagePipeline({
  config,
  insightsApp,
  onToggle,
  onPreset,
  onPromptChange,
  onTableauOutputsChange,
}: {
  config: RunConfig;
  // PR 2 (2026-06-03): SI / Service-Insights packs stay on the
  // ATF deploy chain (Phase 6) and hide the new direct-Tab-Next
  // tile (Phase 8). Null/undefined → non-SI pack → tile visible.
  insightsApp?: "sales_insights" | "service_insights" | null;
  onToggle: (k: keyof RunConfig["pipeline"]) => void;
  onPreset: (preset: RunConfig["pipeline"]) => void;
  onPromptChange: (v: string) => void;
  onTableauOutputsChange: (next: TableauOutputsConfig) => void;
}): JSX.Element {
  const isRefresh = config.mode === "refresh";
  const groups = (
    ["data", "insight", "activation"] as ("data" | "insight" | "activation")[]
  ).map((layer) => ({
    layer,
    meta: LAYER_META[layer],
    steps: PIPELINE_STEPS.filter((s) => s.layer === layer),
  }));

  const satisfies = (id: Layer): boolean =>
    (PIPELINE_DEPS[id] ?? []).every((d) => config.pipeline[d]);

  // CRM and TDSX runs cannot share a single pipeline today — the worker
  // either provisions a Salesforce org (CRM) OR builds a .tdsx file
  // (output_mode=hyper). Enforce mutual exclusion in the UI.
  const isMutexBlocked = (id: Layer): boolean => {
    const tdsxOn = (config.pipeline as Record<string, boolean>).tdsx === true;
    if (id === "tdsx") {
      return (
        config.pipeline.crm ||
        config.pipeline.dc ||
        config.pipeline.sdm ||
        config.pipeline.apps ||
        config.pipeline.tabnext
      );
    }
    return tdsxOn;
  };

  // PR 2 (2026-06-03): Tab Next tile drives Phase 8 (direct authoring)
  // for non-SI packs. SI / Service-Insights packs stay on Phase 6 (ATF)
  // — the tile would 422 server-side anyway, so hide it client-side.
  const isHiddenByPack = (id: Layer): boolean => {
    if (id === "tabnext" && (
      insightsApp === "sales_insights" || insightsApp === "service_insights"
    )) {
      return true;
    }
    return false;
  };

  return (
    <div>
      <StageHeader
        title={
          isRefresh ? "What do you want to refresh?" : "Choose pipeline targets"
        }
        blurb={
          isRefresh
            ? "Pick the layers to refresh on this existing org. No data reshape — streams re-run, dashboards reconnect."
            : "Pick the layers to build. Some steps depend on earlier ones — we'll grey them out until their deps are met."
        }
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 12,
        }}
      >
        {groups.map(({ layer, meta, steps }, idx) => (
          <div
            key={layer}
            style={{
              padding: 16,
              borderRadius: 12,
              background: meta.bg,
              border: `1px solid ${meta.border}`,
              position: "relative",
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <div
                className="label"
                style={{ color: meta.fg }}
              >
                Layer {idx + 1}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--ink)",
                  marginTop: 2,
                }}
              >
                {meta.label}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--ink-2)",
                  marginTop: 2,
                }}
              >
                {meta.blurb}
              </div>
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {steps
                .filter((s) => !isHiddenByPack(s.id as Layer))
                .map((s) => {
                const id = s.id as Layer;
                const on = config.pipeline[id];
                const blockedByMutex = isMutexBlocked(id);
                // ``comingSoon`` tiles render disabled regardless of
                // dependency / mutex state and wear a yellow badge so
                // users know the feature is queued, not broken.
                // (Currently no tiles ship ``comingSoon`` — kept as
                // reusable infrastructure for any future deferred
                // tile.)
                const isComingSoon = !!s.comingSoon;
                const avail = satisfies(id) && !blockedByMutex && !isComingSoon;
                const tooltip = isComingSoon
                  ? `${s.label} ships ${s.comingSoon}.`
                  : blockedByMutex
                    ? id === "tdsx"
                      ? "TDSX builds run on their own — clear other layers first."
                      : "TDSX is selected — clear it to add CRM/Data 360 layers."
                    : satisfies(id)
                      ? undefined
                      : `Requires ${(PIPELINE_DEPS[id] ?? []).join(", ")}.`;
                return (
                  <button
                    key={s.id}
                    onClick={() => avail && onToggle(id)}
                    disabled={!avail}
                    title={tooltip}
                    style={{
                      padding: 10,
                      borderRadius: 8,
                      textAlign: "left",
                      border: `1px solid ${on ? meta.fg : "var(--bd)"}`,
                      background: on ? "white" : "rgba(255,255,255,.7)",
                      opacity: avail ? 1 : 0.5,
                      cursor: avail ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      position: "relative",
                    }}
                  >
                    <Checkbox on={on} color={meta.fg} size={14} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--ink)",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span>{s.label}</span>
                        {isComingSoon && (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              padding: "1px 6px",
                              borderRadius: 999,
                              background: "rgba(245,166,35,.18)",
                              color: "#8a5200",
                              letterSpacing: 0.4,
                              textTransform: "uppercase",
                            }}
                          >
                            {s.comingSoon}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--ink-2)",
                          marginTop: 1,
                        }}
                      >
                        {s.desc}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {idx < groups.length - 1 && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  right: -8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--cb-80)",
                  fontSize: 18,
                  zIndex: 1,
                }}
              >
                →
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Preset shortcuts */}
      <div
        style={{
          marginTop: 18,
          padding: 12,
          background: "var(--cb-98)",
          borderRadius: 10,
          display: "flex",
          gap: 14,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          className="label"
          style={{ color: "var(--ink-3)" }}
        >
          Shortcut
        </div>
        <button
          className="btn ghost sm"
          onClick={() => onPreset(PIPELINE_PRESETS.crm)}
        >
          CRM only
        </button>
        <button
          className="btn ghost sm"
          onClick={() => onPreset(PIPELINE_PRESETS.tdsx)}
        >
          TDSX only
        </button>
        <button
          className="btn ghost sm"
          onClick={() => onPreset(PIPELINE_PRESETS.full)}
        >
          Full pipeline
        </button>
      </div>

      {/* PR 1 Week 3 (2026-06-01) — Tableau outputs panel reveals
          when the TDSX layer is selected. Lets the SE pick which of
          the 4 Phase 7 sub-steps to run + supply Tableau Cloud creds
          when Publish/Pulse are on. */}
      {config.pipeline.tdsx && (
        <TableauOutputsPanel
          value={config.tableauOutputs ?? DEFAULT_TABLEAU_OUTPUTS}
          onChange={onTableauOutputsChange}
        />
      )}

      {/*
        Surface the manual stream-refresh step before users hit Launch
        so the room understands the workflow — otherwise they'll watch
        the Phase 6 progress bar tick past "Stream Refresh" and assume
        it ran. The pipeline sets ``refresh_data_streams: true`` when
        ``dc`` is on, but production ships with ``SKIP_BROWSER_SETUP=true``
        which makes the auto-refresh step a no-op (saves browser pods,
        avoids flaky Playwright). Users do the refresh by hand in the
        Data 360 Streams UI.
      */}
      {config.pipeline.dc && (
        <div
          role="note"
          style={{
            marginTop: 14,
            padding: "10px 14px",
            borderRadius: 10,
            background: "rgba(245,166,35,.08)",
            border: "1px solid rgba(245,166,35,.35)",
            color: "#5d3b00",
            fontSize: 12,
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>⚙</span>
          <span>
            <strong>Manual step after run:</strong> open Data 360 →
            Data Streams → click <em>Refresh</em> on each stream. We
            don&apos;t auto-refresh in this environment — you&apos;ll
            see the streams fill in live as you click.
          </span>
        </div>
      )}

      <AiCustomPrompt
        value={config.aiCustomPrompt ?? ""}
        onChange={onPromptChange}
      />
    </div>
  );
}

function AiCustomPrompt({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const [focused, setFocused] = useState(false);
  const on = value.length > 0;
  const suggestions = [
    "Generate 3 Exec QBR dashboards after load",
    "Add a handful of escalated cases to top 5 accounts",
    "Create a Close Plan for the largest open opp",
  ];

  return (
    <div
      style={{
        marginTop: 14,
        padding: 16,
        borderRadius: 12,
        border: `1px solid ${focused || on ? "var(--violet)" : "var(--bd)"}`,
        background:
          focused || on
            ? "linear-gradient(135deg, #f6eefd, white 60%)"
            : "white",
        transition: "border-color .15s, background .15s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: "#ede0fb",
            color: "var(--violet)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          ✦
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink)",
            }}
          >
            Anything custom to run after the load?
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>
            Generator will execute this pass once records are loaded. Natural
            language is fine.
          </div>
        </div>
        <span className="chip ghost" style={{ fontSize: 10 }}>
          Optional
        </span>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="e.g. after loading, generate 3 custom dashboards for the Exec QBR scenario…"
        rows={2}
        style={{
          width: "100%",
          resize: "vertical",
          minHeight: 52,
          padding: "10px 12px",
          fontSize: 12,
          lineHeight: 1.55,
          border: "1px solid var(--bd)",
          borderRadius: 8,
          fontFamily: "inherit",
          color: "var(--ink)",
          background: "white",
          outline: "none",
          boxShadow: focused
            ? "0 0 0 3px rgba(144,80,233,.2)"
            : "none",
        }}
      />
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          marginTop: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ink-3)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
            alignSelf: "center",
            marginRight: 4,
          }}
        >
          Try
        </span>
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onChange(s)}
            style={{
              fontSize: 11,
              padding: "4px 10px",
              borderRadius: 999,
              background: "#f6eefd",
              border: "1px solid #d2bcf3",
              color: "var(--violet)",
              cursor: "pointer",
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────── Shared bits ───────────────────

function StageHeader({
  title,
  blurb,
  inline,
}: {
  title: string;
  blurb: string;
  inline?: boolean;
}): JSX.Element {
  return (
    <div style={{ marginBottom: inline ? 0 : 18 }}>
      <h2
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: "var(--ink)",
          margin: "0 0 4px",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: 12,
          color: "var(--ink-2)",
          margin: 0,
          lineHeight: 1.5,
        }}
      >
        {blurb}
      </p>
    </div>
  );
}

function Checkbox({
  on,
  color,
  size = 14,
}: {
  on: boolean;
  color: string;
  size?: number;
}): ReactNode {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        flexShrink: 0,
        background: on ? color : "white",
        border: `1.5px solid ${on ? color : "var(--bd)"}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontSize: Math.round(size * 0.7),
      }}
    >
      {on ? "✓" : ""}
    </span>
  );
}
